<!-- User Guide sections — projected into the shared shell in docs2.component.html.
     Wrapped so sections get the same 16-unit vertical rhythm as the shell's base
     sections (the shell's space-y only reaches its own direct children). -->
<div class="flex flex-col gap-16">

  <!-- ========================= CHOOSE TOOL ======================== -->
  <section id="tools" class="scroll-mt-16">
    <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">User Guide</p>
    <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Choose a tool</h1>
    <p class="text-black leading-relaxed font-light mb-8">
      Pick which tool to read about and its guide opens right below. The <b>USDM Builder</b> and the
      <b>SDTM Builder</b> are two separate, independent tools.
    </p>
    <div class="grid gap-5 md:grid-cols-2">
      <button type="button" (click)="toolSelected.emit('usdm')"
         class="text-left block p-6 rounded-2xl border transition-all duration-300 hover:shadow-lg"
         [class]="tool === 'usdm' ? 'border-[#1594fd] ring-2 ring-[#1594fd]/25 shadow-lg' : 'border-gray-200 hover:border-[#1594fd]'">
        <div class="flex items-center gap-3 mb-3">
          <span class="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#1594fd] text-white">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><path d="M14 2v6h6"/><path d="M9 13l2 2 4-4"/></svg>
          </span>
          <h3 class="text-xl font-semibold text-black">USDM Builder</h3>
        </div>
        <p class="text-sm text-gray-700 leading-relaxed mb-3">Turn a study protocol into a validated Unified Study Data Model &mdash; create a study, extract &amp; map, validate and download.</p>
        <span class="text-sm text-[#1594fd] font-semibold">{{ tool === 'usdm' ? 'Showing below ↓' : 'Open the USDM guide →' }}</span>
      </button>
      <button type="button" (click)="toolSelected.emit('sdtm')"
         class="text-left block p-6 rounded-2xl border transition-all duration-300 hover:shadow-lg"
         [class]="tool === 'sdtm' ? 'border-[#1594fd] ring-2 ring-[#1594fd]/25 shadow-lg' : 'border-gray-200 hover:border-[#1594fd]'">
        <div class="flex items-center gap-3 mb-3">
          <span class="inline-flex items-center justify-center w-11 h-11 rounded-xl bg-[#1594fd] text-white">
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="3" y="3" width="18" height="18" rx="2"/><path d="M3 9h18M3 15h18M9 3v18M15 3v18"/></svg>
          </span>
          <h3 class="text-xl font-semibold text-black">SDTM Builder</h3>
        </div>
        <p class="text-sm text-gray-700 leading-relaxed mb-3">Convert raw study data into the submission-ready SDTM standard &mdash; upload, annotate the CRF, review the spec, generate R and download deliverables.</p>
        <span class="text-sm text-[#1594fd] font-semibold">{{ tool === 'sdtm' ? 'Showing below ↓' : 'Open the SDTM guide →' }}</span>
      </button>
    </div>
  </section>

  @if (tool === 'usdm') {
    <!-- ========================= USDM BUILDER ======================= -->
    <section id="usdm" class="scroll-mt-16">
      <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">USDM Builder</p>
      <h1 class="text-5xl font-bold mb-6 text-black tracking-tight">Build a validated USDM</h1>
      <p class="text-lg text-black leading-relaxed font-light mb-8">
        The <b>USDM Builder</b> turns a study protocol into a submission-ready
        <b>Unified Study Data Model (USDM)</b> in three guided steps, tracked by a stepper at the top of the
        page: <b>Study Creation</b>, <b>Extraction &amp; Mapping</b> and <b>Validation</b>. You stay in control
        at every step &mdash; nothing is assembled without your review.
      </p>

      <div class="rounded-2xl border border-gray-200 p-6 bg-white mb-8">
        <h3 class="text-sm font-bold uppercase tracking-wider text-gray-500 mb-6">The USDM pipeline</h3>

        <div class="-mx-1 overflow-x-auto px-1 pb-1">
          <ol class="relative flex min-w-max items-start gap-2 sm:min-w-0 sm:justify-between">
            <span aria-hidden="true" class="absolute left-[16%] right-[16%] top-5 -z-0 h-0.5 bg-gray-200"></span>
            <li class="z-10 flex flex-1 flex-col items-center px-1 text-center">
              <span class="flex h-10 w-10 items-center justify-center rounded-full bg-[#1594fd] text-white text-sm font-semibold ring-4 ring-white">1</span>
              <span class="mt-2 text-sm font-semibold text-black whitespace-nowrap">Study Creation</span>
              <span class="text-xs text-gray-500 whitespace-nowrap">Study &amp; protocol</span>
            </li>
            <li class="z-10 flex flex-1 flex-col items-center px-1 text-center">
              <span class="flex h-10 w-10 items-center justify-center rounded-full bg-[#1594fd] text-white text-sm font-semibold ring-4 ring-white">2</span>
              <span class="mt-2 text-sm font-semibold text-black whitespace-nowrap">Extraction &amp; Mapping</span>
              <span class="text-xs text-gray-500 whitespace-nowrap">Map to USDM</span>
            </li>
            <li class="z-10 flex flex-1 flex-col items-center px-1 text-center">
              <span class="flex h-10 w-10 items-center justify-center rounded-full bg-[#1594fd] text-white text-sm font-semibold ring-4 ring-white">3</span>
              <span class="mt-2 text-sm font-semibold text-black whitespace-nowrap">Validation</span>
              <span class="text-xs text-gray-500 whitespace-nowrap">Validate &amp; download</span>
            </li>
          </ol>
        </div>

        <dl class="mt-8 grid gap-x-8 gap-y-4 sm:grid-cols-3">
          <div class="flex gap-3">
            <span class="flex-shrink-0 w-6 h-6 mt-0.5 rounded-full bg-blue-50 text-[#1594fd] text-xs font-bold flex items-center justify-center">1</span>
            <div><dt class="font-semibold text-black">Study Creation</dt><dd class="text-sm text-gray-700 leading-relaxed">Create or connect a study and upload the protocol PDF plus OSB details.</dd></div>
          </div>
          <div class="flex gap-3">
            <span class="flex-shrink-0 w-6 h-6 mt-0.5 rounded-full bg-blue-50 text-[#1594fd] text-xs font-bold flex items-center justify-center">2</span>
            <div><dt class="font-semibold text-black">Extraction &amp; Mapping</dt><dd class="text-sm text-gray-700 leading-relaxed">The agent extracts study elements; you review and validate each one.</dd></div>
          </div>
          <div class="flex gap-3">
            <span class="flex-shrink-0 w-6 h-6 mt-0.5 rounded-full bg-blue-50 text-[#1594fd] text-xs font-bold flex items-center justify-center">3</span>
            <div><dt class="font-semibold text-black">Validation</dt><dd class="text-sm text-gray-700 leading-relaxed">Validate the assembled USDM, then download it or open the viewer.</dd></div>
          </div>
        </dl>
      </div>

      <h2 class="text-2xl font-semibold mb-4 text-black">What you'll need</h2>
      <div class="overflow-x-auto rounded-xl border border-gray-200 mb-10">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
            <tr>
              <th class="px-4 py-3 font-semibold">Input</th>
              <th class="px-4 py-3 font-semibold">Required</th>
              <th class="px-4 py-3 font-semibold">What it is used for</th>
            </tr>
          </thead>
          <tbody class="text-black">
            <tr class="border-t border-gray-100">
              <td class="px-4 py-3 font-semibold">Protocol PDF</td>
              <td class="px-4 py-3"><span class="text-red-600 font-semibold">Yes</span></td>
              <td class="px-4 py-3 text-gray-700">The source the agent extracts study elements from.</td>
            </tr>
            <tr class="border-t border-gray-100">
              <td class="px-4 py-3 font-semibold">OSB server URL</td>
              <td class="px-4 py-3"><span class="text-red-600 font-semibold">Yes</span></td>
              <td class="px-4 py-3 text-gray-700">The OpenStudyBuilder instance the study is created against.</td>
            </tr>
            <tr class="border-t border-gray-100">
              <td class="px-4 py-3 font-semibold">OSB Study ID</td>
              <td class="px-4 py-3 text-gray-500">Optional</td>
              <td class="px-4 py-3 text-gray-700">Provide it if the study already exists in OSB.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <h2 class="text-2xl font-semibold mb-4 text-black">Open the USDM Builder</h2>
      <p class="text-black leading-relaxed font-light mb-6">
        Open it from your profile icon at the top-right of any page &mdash; select <b>USDM</b> from the dropdown.
      </p>
      <img src="assets/quickstart2/open-usdm.png" alt="Open USDM from the profile menu"
           class="rounded-lg shadow-lg border border-gray-200 mb-10 w-full">

      <h2 class="text-2xl font-semibold mb-4 text-black">Step 1 &middot; Study Creation</h2>
      <p class="text-black leading-relaxed font-light mb-6">
        On the <b>Cloud Studies</b> tab, enter a name to create a new study, or pick an existing one to connect
        to it. This is the container that holds your protocol and generated artifacts.
      </p>
      <img src="assets/quickstart2/usdm-study-creation.png" alt="Study creation"
           class="rounded-lg shadow-lg border border-gray-200 mb-8 w-full">
      <p class="text-black leading-relaxed font-light mb-6">
        Then, under <b>Select or Upload Protocol</b>, upload the <b>protocol PDF</b> and supply the
        <b>OSB Server URL</b> (and an <b>OSB Study ID</b> if you already have one). Submit to move on to extraction.
      </p>
      <img src="assets/quickstart2/usdm-protocol-upload.png" alt="Upload protocol and OSB details"
           class="rounded-lg shadow-lg border border-gray-200 mb-10 w-full">

      <h2 class="text-2xl font-semibold mb-4 text-black">Step 2 &middot; Extraction &amp; Mapping</h2>
      <p class="text-black leading-relaxed font-light mb-6">
        The protocol is broken into <b>states</b> (study elements). Tick <b>Select all</b> and click
        <b>Extract All</b> to process everything, or use <b>Extract</b> on individual states. You can stop a run
        at any time.
      </p>
      <img src="assets/quickstart2/usdm-extraction.png" alt="Extraction and mapping states"
           class="rounded-lg shadow-lg border border-gray-200 mb-8 w-full">
      <p class="text-black leading-relaxed font-light mb-6">
        Click <b>View / Validate</b> on a state to open a dialog with the <b>PDF viewer</b>, the
        <b>extracted data</b> and the <b>agent's thought process</b> &mdash; so you can confirm each mapping
        before moving on. When every state is complete, click <b>Next</b>.
      </p>
      <img src="assets/quickstart2/usdm-view-validate.png" alt="View and validate an extracted state"
           class="rounded-lg shadow-lg border border-gray-200 mb-10 w-full">

      <h2 class="text-2xl font-semibold mb-4 text-black">Step 3 &middot; Validation &amp; Download</h2>
      <p class="text-black leading-relaxed font-light mb-6">
        Step 3 validates the assembled USDM against the standard and reports the results. Wait for validation
        to finish.
      </p>
      <img src="assets/quickstart2/usdm-validation.png" alt="USDM validation"
           class="rounded-lg shadow-lg border border-gray-200 mb-8 w-full">
      <p class="text-black leading-relaxed font-light mb-6">
        Once validation passes, click <b>Download USDM</b> to save the file, or <b>Open Viewer</b> to explore it
        right away.
      </p>
      <img src="assets/quickstart2/usdm-download.png" alt="Download the validated USDM file"
           class="rounded-lg shadow-lg border border-gray-200 mb-6 w-full">
      <div class="rounded-xl border-l-4 border-emerald-400 bg-emerald-50 px-5 py-4">
        <p class="text-sm text-black leading-relaxed">
          That's the full USDM workflow &mdash; from protocol to a validated, downloadable USDM file. Next,
          inspect it in the <b>USDM Viewer</b>.
        </p>
      </div>
    </section>

    <!-- ========================= USDM VIEWER ======================== -->
    <section id="usdm-viewer" class="scroll-mt-16">
      <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">USDM Viewer</h1>
      <p class="text-black leading-relaxed font-light mb-6">
        The USDM Viewer renders your generated USDM JSON in a structured, readable layout so you can inspect
        objectives, endpoints, visits and other study elements without wading through raw JSON. From Step 3
        (or the download step) choose <b>Open Viewer</b> to open it.
      </p>
      <img src="assets/quickstart2/usdm-viewer.png" alt="USDM JSON Viewer"
           class="rounded-lg shadow-lg border border-gray-200 w-full">
      <div class="rounded-xl border-l-4 border-[#1594fd] bg-blue-50 px-5 py-4 mt-6">
        <p class="text-sm text-black leading-relaxed">
          That's the complete USDM Builder workflow. The <b>SDTM Builder</b> &mdash; the platform's other tool
          &mdash; converts raw study data into submission-ready SDTM, and the validated USDM you just built can
          optionally feed its <b>Trial Design</b> step. Pick it from <b>Choose a tool</b> above to read its guide.
        </p>
      </div>
    </section>

    <!-- ========================= FAQ (USDM) ========================= -->
    <section id="faq-usdm" class="scroll-mt-16">
      <h1 class="text-4xl font-bold mb-8 text-black tracking-tight">FAQ</h1>
      <h3 class="text-sm font-bold uppercase tracking-wider text-gray-400 mb-4">USDM</h3>
      <div class="grid gap-5 md:grid-cols-2">
        <div class="p-6 rounded-xl border border-gray-200 hover:border-[#1594fd] transition-all duration-300 hover:shadow-lg">
          <h3 class="text-lg font-semibold mb-2 text-black">How do I create an account?</h3>
          <p class="text-sm text-gray-700 leading-relaxed">Open the sign-up page, enter your name, email and a password (8+ characters), confirm it, then click <b>Sign up</b>.</p>
        </div>
        <div class="p-6 rounded-xl border border-gray-200 hover:border-[#1594fd] transition-all duration-300 hover:shadow-lg">
          <h3 class="text-lg font-semibold mb-2 text-black">Why do I need an access code?</h3>
          <p class="text-sm text-gray-700 leading-relaxed">Clinical AI Studio is in closed beta, so the Access Required screen appears right after sign-in. Request a code from <b>info&#64;ailens.ai</b>.</p>
        </div>
        <div class="p-6 rounded-xl border border-gray-200 hover:border-[#1594fd] transition-all duration-300 hover:shadow-lg">
          <h3 class="text-lg font-semibold mb-2 text-black">Why do I need a repository?</h3>
          <p class="text-sm text-gray-700 leading-relaxed">A repository is the cloud folder that holds a study's files and results. Every tool &mdash; chat, workspace, the USDM Builder and the SDTM Builder &mdash; runs against a connected repository.</p>
        </div>
        <div class="p-6 rounded-xl border border-gray-200 hover:border-[#1594fd] transition-all duration-300 hover:shadow-lg">
          <h3 class="text-lg font-semibold mb-2 text-black">What do I need to build a USDM?</h3>
          <p class="text-sm text-gray-700 leading-relaxed">A study protocol PDF, your OSB server URL, and optionally an OSB Study ID if the study already exists in OSB.</p>
        </div>
        <div class="p-6 rounded-xl border border-gray-200 hover:border-[#1594fd] transition-all duration-300 hover:shadow-lg">
          <h3 class="text-lg font-semibold mb-2 text-black">How do I check the extraction is correct?</h3>
          <p class="text-sm text-gray-700 leading-relaxed">Use <b>View / Validate</b> on each state to compare the extracted data against the protocol PDF alongside the agent's reasoning before continuing.</p>
        </div>
        <div class="p-6 rounded-xl border border-gray-200 hover:border-[#1594fd] transition-all duration-300 hover:shadow-lg">
          <h3 class="text-lg font-semibold mb-2 text-black">How do I get my USDM file?</h3>
          <p class="text-sm text-gray-700 leading-relaxed">After validation completes in Step 3, click <b>Download USDM</b> to save the file, or <b>Open Viewer</b> to inspect it in the structured viewer.</p>
        </div>
      </div>
    </section>
  } @else if (tool === 'sdtm') {
    <!-- ======================== SDTM OVERVIEW ======================= -->
    <section id="sdtm-overview" class="scroll-mt-16">
      <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">SDTM Builder</p>
      <h1 class="text-5xl font-bold mb-6 text-black tracking-tight">Turn a CRF into SDTM</h1>
      <p class="text-lg text-black leading-relaxed font-light mb-6">
        The <b>SDTM Builder</b> takes what every study already has &mdash; the
        <b>Annotated Layout Spec (ALS)</b>, the <b>blank CRF</b> and your <b>raw datasets</b> &mdash; and
        walks them through to submission-ready output: an annotated CRF, a reviewed mapping specification,
        generated R code, and derived SDTM datasets. You stay in control at every step; nothing is
        published without your approval.
      </p>
      <p class="text-black leading-relaxed font-light mb-6">
        Each input feeds a different step: the ALS supplies the form and field list, the CRF supplies the
        pages annotations are placed on, and the <b>raw datasets</b> are what code generation reads to
        derive each SDTM domain. A <b>USDM</b> study definition is optional &mdash; it is only used to build
        the Trial Design datasets, which you can skip. If you built one with the USDM Builder, this is where it is used.
      </p>
      <p class="text-black leading-relaxed font-light mb-6">
        The route through it is <b>Studies workspace &rarr; New Study &rarr; Upload &rarr; Annotation
        &rarr; Spec Validation &rarr; Codegen &rarr; Results</b>, presented as a horizontal timeline
        hub with gated per-step panels.
      </p>

      <div class="rounded-2xl border border-gray-200 p-6 bg-white mb-6">
        <h3 class="text-sm font-bold uppercase tracking-wider text-gray-500 mb-6">The pipeline</h3>

        <!-- Horizontal timeline, mirroring the six nodes shown in the app -->
        <div class="-mx-1 overflow-x-auto px-1 pb-1">
          <ol class="relative flex min-w-max items-start gap-2 sm:min-w-0 sm:justify-between">
            <span aria-hidden="true" class="absolute left-[8%] right-[8%] top-5 -z-0 h-0.5 bg-gray-200"></span>

            <li class="z-10 flex flex-1 flex-col items-center px-1 text-center">
              <span class="flex h-10 w-10 items-center justify-center rounded-full bg-[#1594fd] text-white text-sm font-semibold ring-4 ring-white">1</span>
              <span class="mt-2 text-sm font-semibold text-black whitespace-nowrap">Upload</span>
              <span class="text-xs text-gray-500 whitespace-nowrap">Study inputs</span>
            </li>
            <li class="z-10 flex flex-1 flex-col items-center px-1 text-center">
              <span class="flex h-10 w-10 items-center justify-center rounded-full bg-[#1594fd] text-white text-sm font-semibold ring-4 ring-white">2</span>
              <span class="mt-2 text-sm font-semibold text-black whitespace-nowrap">Trial Design</span>
              <span class="text-xs text-gray-500 whitespace-nowrap">TA/TE/TV/TI/TS</span>
            </li>
            <li class="z-10 flex flex-1 flex-col items-center px-1 text-center">
              <span class="flex h-10 w-10 items-center justify-center rounded-full bg-[#1594fd] text-white text-sm font-semibold ring-4 ring-white">3</span>
              <span class="mt-2 text-sm font-semibold text-black whitespace-nowrap">Annotation</span>
              <span class="text-xs text-gray-500 whitespace-nowrap">CRF prediction</span>
            </li>
            <li class="z-10 flex flex-1 flex-col items-center px-1 text-center">
              <span class="flex h-10 w-10 items-center justify-center rounded-full bg-[#1594fd] text-white text-sm font-semibold ring-4 ring-white">4</span>
              <span class="mt-2 text-sm font-semibold text-black whitespace-nowrap">Spec</span>
              <span class="text-xs text-gray-500 whitespace-nowrap">Review &amp; approve</span>
            </li>
            <li class="z-10 flex flex-1 flex-col items-center px-1 text-center">
              <span class="flex h-10 w-10 items-center justify-center rounded-full bg-[#1594fd] text-white text-sm font-semibold ring-4 ring-white">5</span>
              <span class="mt-2 text-sm font-semibold text-black whitespace-nowrap">Codegen</span>
              <span class="text-xs text-gray-500 whitespace-nowrap">Generate R</span>
            </li>
            <li class="z-10 flex flex-1 flex-col items-center px-1 text-center">
              <span class="flex h-10 w-10 items-center justify-center rounded-full bg-[#1594fd] text-white text-sm font-semibold ring-4 ring-white">6</span>
              <span class="mt-2 text-sm font-semibold text-black whitespace-nowrap">Results</span>
              <span class="text-xs text-gray-500 whitespace-nowrap">Deliverables</span>
            </li>
          </ol>
        </div>

        <!-- What each node does -->
        <dl class="mt-8 grid gap-x-8 gap-y-4 sm:grid-cols-2">
          <div class="flex gap-3">
            <span class="flex-shrink-0 w-6 h-6 mt-0.5 rounded-full bg-blue-50 text-[#1594fd] text-xs font-bold flex items-center justify-center">1</span>
            <div><dt class="font-semibold text-black">Upload</dt><dd class="text-sm text-gray-700 leading-relaxed">Give the study its ALS and CRF, plus the raw datasets code generation will derive from.</dd></div>
          </div>
          <div class="flex gap-3">
            <span class="flex-shrink-0 w-6 h-6 mt-0.5 rounded-full bg-blue-50 text-[#1594fd] text-xs font-bold flex items-center justify-center">2</span>
            <div><dt class="font-semibold text-black">Trial Design</dt><dd class="text-sm text-gray-700 leading-relaxed">Optional. Build TA/TE/TV/TI/TS datasets from a USDM file.</dd></div>
          </div>
          <div class="flex gap-3">
            <span class="flex-shrink-0 w-6 h-6 mt-0.5 rounded-full bg-blue-50 text-[#1594fd] text-xs font-bold flex items-center justify-center">3</span>
            <div><dt class="font-semibold text-black">Annotation</dt><dd class="text-sm text-gray-700 leading-relaxed">AI predicts an SDTM target for every CRF field; you confirm, correct or reject each one, then generate the annotated PDF.</dd></div>
          </div>
          <div class="flex gap-3">
            <span class="flex-shrink-0 w-6 h-6 mt-0.5 rounded-full bg-blue-50 text-[#1594fd] text-xs font-bold flex items-center justify-center">4</span>
            <div><dt class="font-semibold text-black">Spec</dt><dd class="text-sm text-gray-700 leading-relaxed">Edit the generated mapping spec and approve it.</dd></div>
          </div>
          <div class="flex gap-3">
            <span class="flex-shrink-0 w-6 h-6 mt-0.5 rounded-full bg-blue-50 text-[#1594fd] text-xs font-bold flex items-center justify-center">5</span>
            <div><dt class="font-semibold text-black">Codegen</dt><dd class="text-sm text-gray-700 leading-relaxed">An agent writes and runs R (sdtm.oak) per domain, correcting itself as it goes.</dd></div>
          </div>
          <div class="flex gap-3">
            <span class="flex-shrink-0 w-6 h-6 mt-0.5 rounded-full bg-blue-50 text-[#1594fd] text-xs font-bold flex items-center justify-center">6</span>
            <div><dt class="font-semibold text-black">Results</dt><dd class="text-sm text-gray-700 leading-relaxed">Download everything and mark the study complete.</dd></div>
          </div>
        </dl>
      </div>

      <div class="rounded-xl border-l-4 border-[#1594fd] bg-blue-50 px-5 py-4 mb-4">
        <p class="text-sm text-black leading-relaxed">
          <b>Step gating.</b> Each step stays locked until the one before it finishes, so you
          can never generate code from a spec you have not approved, or approve a spec before annotation
          has produced one. Trial Design is the single exception &mdash; it is a <b>side workflow</b> rather
          than a gating step, so it can be skipped entirely.
        </p>
      </div>

      <p class="text-black leading-relaxed font-light mb-3">
        Opening a study takes you to its <b>pipeline hub</b>, which shows this timeline above the panel for
        whichever step is selected, under a header
        carrying the protocol ID, a Draft / Active / Completed badge, the study name, phase and sponsor. The
        page opens on the first step needing your attention. Click any unlocked node to jump to it; locked
        nodes cannot be clicked and say what will unlock them. While anything is running the page refreshes
        itself every few seconds, so progress appears without reloading.
      </p>
      <p class="text-black leading-relaxed font-light">
        One deliberate exception: when annotation finishes, the page keeps you on Annotation rather than
        jumping ahead, so you get the chance to review predictions. Move on with <b>Continue to Spec</b>
        when you are ready.
      </p>
    </section>

    <!-- ======================== REQUIREMENTS ======================== -->
    <section id="requirements" class="scroll-mt-16">
      <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Before you begin</h1>
      <p class="text-black leading-relaxed font-light mb-8">
        Three things must be true before the SDTM screens will open. If any one of them is missing you
        are returned to the home page with a message explaining which.
      </p>

      <div class="space-y-4 mb-10">
        <div class="rounded-xl border border-gray-200 p-5">
          <h3 class="font-semibold text-black mb-1">1. You are signed in</h3>
          <p class="text-sm text-gray-700 leading-relaxed">
            Your session is restored automatically when you return, and refreshed in the background about
            a minute before it would expire, so a long working session will not drop you mid-run. If the
            session cannot be restored you are sent to the home page.
          </p>
        </div>
        <div class="rounded-xl border border-gray-200 p-5">
          <h3 class="font-semibold text-black mb-1">2. A repository is connected</h3>
          <p class="text-sm text-gray-700 leading-relaxed">
            Every SDTM study lives inside a connected repository, which is where uploaded files and
            generated outputs are written. Without one you see
            <span class="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">Please connect the repository to access SDTM</span>
            and are redirected. Connect a repository from the dashboard before opening SDTM.
          </p>
        </div>
        <div class="rounded-xl border border-gray-200 p-5">
          <h3 class="font-semibold text-black mb-1">3. You have the study documents</h3>
          <p class="text-sm text-gray-700 leading-relaxed">
            Three inputs are required: the <b>ALS</b>, the <b>blank CRF</b> and your <b>raw datasets</b>.
            Gather them before you start &mdash; each is needed by a later step of the pipeline. The
            <b>USDM</b> study definition is optional, and only needed if you want Trial Design datasets.
          </p>
        </div>
      </div>

      <h2 class="text-2xl font-semibold mb-4 text-black">What to have ready</h2>
      <div class="overflow-x-auto rounded-xl border border-gray-200">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
            <tr>
              <th class="px-4 py-3 font-semibold">Document</th>
              <th class="px-4 py-3 font-semibold">Format</th>
              <th class="px-4 py-3 font-semibold">Required</th>
              <th class="px-4 py-3 font-semibold">What it is used for</th>
            </tr>
          </thead>
          <tbody class="text-black">
            <tr class="border-t border-gray-100">
              <td class="px-4 py-3 font-semibold">ALS</td>
              <td class="px-4 py-3 font-mono text-xs">.xlsx</td>
              <td class="px-4 py-3"><span class="text-red-600 font-semibold">Yes</span></td>
              <td class="px-4 py-3 text-gray-700">Supplies the form and field list that everything else is built from.</td>
            </tr>
            <tr class="border-t border-gray-100">
              <td class="px-4 py-3 font-semibold">Blank CRF</td>
              <td class="px-4 py-3 font-mono text-xs">.pdf</td>
              <td class="px-4 py-3"><span class="text-red-600 font-semibold">Yes</span></td>
              <td class="px-4 py-3 text-gray-700">The pages annotations are placed onto.</td>
            </tr>
            <tr class="border-t border-gray-100">
              <td class="px-4 py-3 font-semibold">Raw data</td>
              <td class="px-4 py-3 font-mono text-xs">any, multiple</td>
              <td class="px-4 py-3"><span class="text-red-600 font-semibold">Yes</span></td>
              <td class="px-4 py-3 text-gray-700">Source datasets the code generation agent reads when deriving a domain.</td>
            </tr>
            <tr class="border-t border-gray-100">
              <td class="px-4 py-3 font-semibold">USDM</td>
              <td class="px-4 py-3 font-mono text-xs">.json</td>
              <td class="px-4 py-3 text-gray-500">Optional</td>
              <td class="px-4 py-3 text-gray-700">Study definition. Only needed for Trial Design datasets.</td>
            </tr>
          </tbody>
        </table>
      </div>
      <p class="mt-4 text-sm text-gray-600 leading-relaxed">
        No USDM file? You can generate one from your protocol PDF using the <b>USDM Builder</b> &mdash; there
        is a direct link on the Upload step &mdash; or simply skip Trial Design.
      </p>
    </section>

    <!-- ========================= OPEN SDTM ========================== -->
    <section id="open-sdtm" class="scroll-mt-16">
      <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Open SDTM</h1>
      <p class="text-black leading-relaxed font-light mb-6">
        With a study connected, click your name in the top-right corner and choose <b>SDTM</b> from the
        menu. The same menu is where you reach Workspace, USDM, SAP AI, Synthetic Data and TFL AI.
      </p>
      <img src="assets/docs2/01-profile-menu-sdtm.png" alt="Profile menu with the SDTM entry"
           class="rounded-lg shadow-lg border border-gray-200 mb-6 w-full">
      <p class="text-black leading-relaxed font-light">
        SDTM switches to a light theme while you are inside it, and returns to your previous theme when
        you leave. This is expected, not a glitch.
      </p>
    </section>

    <!-- ========================== STUDIES =========================== -->
    <section id="studies" class="scroll-mt-16">
      <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Studies workspace</h1>
      <p class="text-black leading-relaxed font-light mb-6">
        The first screen in the SDTM Builder, headed <b>SDTM Studies</b>, lists every study you have,
        newest activity first.
      </p>
      <img src="assets/docs2/02-sdtm-studies-list.png" alt="Studies workspace with search, filters and the pipeline dots"
           class="rounded-lg shadow-lg border border-gray-200 mb-8 w-full">

      <div class="grid gap-4 md:grid-cols-2 mb-8">
        <div class="rounded-xl border border-gray-200 p-5">
          <h3 class="font-semibold text-black mb-2">Search</h3>
          <p class="text-sm text-gray-700 leading-relaxed">Filters as you type, across protocol ID, study name and sponsor.</p>
        </div>
        <div class="rounded-xl border border-gray-200 p-5">
          <h3 class="font-semibold text-black mb-2">Status filter</h3>
          <p class="text-sm text-gray-700 leading-relaxed"><b>All</b>, <b>Draft</b>, <b>Active</b> or <b>Completed</b>. Combines with the search box.</p>
        </div>
      </div>

      <h2 class="text-2xl font-semibold mb-4 text-black">Reading a row</h2>
      <p class="text-black leading-relaxed font-light mb-4">
        Each row shows the protocol ID and name, phase, sponsor, status, the step it is sitting at, a
        five-dot pipeline indicator, the next action, and when it last changed.
      </p>
      <p class="text-black leading-relaxed font-light mb-4">
        The <b>five dots</b> are the five pipeline steps in order. Hover any dot for its name and state.
        Green means finished, a pulsing blue dot means something is running right now, a hollow ringed dot
        means ready for you, red means failed, amber means it needs re-running, and grey means still locked.
      </p>
      <p class="text-black leading-relaxed font-light mb-8">
        <b>Next action</b> tells you exactly what to do next &mdash; <i>Continue: Annotation</i>,
        <i>Running&hellip;</i>, <i>Retry Codegen</i>, or <i>Signed off</i> when the study is finished.
        Click anywhere on the row to open it.
      </p>

      <div class="rounded-xl border-l-4 border-red-400 bg-red-50 px-5 py-4">
        <p class="text-sm text-black leading-relaxed">
          <b>Deleting is permanent.</b> The trash icon on a row asks for confirmation first, then removes
          the study <i>and all of its workspace files</i> &mdash; uploads, spec, trial design and generated
          outputs. There is no undo. Press <span class="font-mono text-xs bg-white px-1.5 py-0.5 rounded border">Esc</span> to back out of the dialog.
        </p>
      </div>
    </section>

    <!-- ========================= NEW STUDY ========================== -->
    <section id="new-study" class="scroll-mt-16">
      <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Create a study</h1>
      <p class="text-black leading-relaxed font-light mb-6">
        Click <b>New Study</b>, then fill the fields according to your study.
      </p>
      <img src="assets/docs2/03-new-study-empty.png" alt="Empty New SDTM Study form"
           class="rounded-lg shadow-lg border border-gray-200 mb-8 w-full">

      <div class="overflow-x-auto rounded-xl border border-gray-200 mb-6">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
            <tr>
              <th class="px-4 py-3 font-semibold">Field</th>
              <th class="px-4 py-3 font-semibold">Required</th>
              <th class="px-4 py-3 font-semibold">Notes</th>
            </tr>
          </thead>
          <tbody class="text-black">
            <tr class="border-t border-gray-100">
              <td class="px-4 py-3 font-semibold">Protocol ID</td>
              <td class="px-4 py-3"><span class="text-red-600 font-semibold">Yes</span></td>
              <td class="px-4 py-3 text-gray-700">Your protocol number, for example ABC-123. The Create button stays disabled until this is filled in.</td>
            </tr>
            <tr class="border-t border-gray-100">
              <td class="px-4 py-3 font-semibold">Study name</td>
              <td class="px-4 py-3"><span class="text-red-600 font-semibold">Yes</span></td>
              <td class="px-4 py-3 text-gray-700">Falls back to the protocol ID if left blank.</td>
            </tr>
            <tr class="border-t border-gray-100">
              <td class="px-4 py-3 font-semibold">Sponsor</td>
              <td class="px-4 py-3"><span class="text-red-600 font-semibold">Yes</span></td>
              <td class="px-4 py-3 text-gray-700">Shown in the studies workspace and the study header.</td>
            </tr>
            <tr class="border-t border-gray-100">
              <td class="px-4 py-3 font-semibold">Phase</td>
              <td class="px-4 py-3"><span class="text-red-600 font-semibold">Yes</span></td>
              <td class="px-4 py-3 text-gray-700">Phase 1 to Phase 4. Defaults to Phase 2.</td>
            </tr>
            <tr class="border-t border-gray-100">
              <td class="px-4 py-3 font-semibold">SDTM IG / MSG / CT</td>
              <td class="px-4 py-3"><span class="text-red-600 font-semibold">Yes</span></td>
              <td class="px-4 py-3 text-gray-700">The CDISC standard versions this study is mapped against. The lists are populated from the installed Standards Library, and the newest version of each is pre-selected.</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="rounded-xl border-l-4 border-amber-400 bg-amber-50 px-5 py-4 mb-4">
        <p class="text-sm text-black leading-relaxed">
          If you see <i>&ldquo;Select a workspace repository first&rdquo;</i>, no repository is connected &mdash; go back to
          the dashboard and connect one. If you see
          <i>&ldquo;CDISC catalog unavailable&rdquo;</i>, the version dropdowns could not be loaded and your
          administrator needs to check the CDISC Library credentials; you can still create the study.
        </p>
      </div>

      <h2 class="text-2xl font-semibold mb-3 text-black mt-8">Worked example</h2>
      <p class="text-black leading-relaxed font-light mb-4">
        The study followed through the rest of this guide was created exactly like this &mdash; protocol ID
        <b>pink</b>, sponsor <b>test</b>, Phase 2, against SDTM IG 3.4, MSG 2.0 and CT 2026-03-27.
      </p>
      <img src="assets/docs2/04-new-study-filled.png" alt="New study form completed with protocol ID pink and sponsor test"
           class="rounded-lg shadow-lg border border-gray-200 mb-6 w-full">
      <p class="text-black leading-relaxed font-light">
        Click <b>Create study</b> and you land directly on the new study's pipeline, with Upload waiting.
      </p>
    </section>

    <!-- =========================== UPLOAD =========================== -->
    <section id="upload" class="scroll-mt-16">
      <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">Step 1</p>
      <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Upload study inputs</h1>
      <p class="text-black leading-relaxed font-light mb-6">
        Four cards, one per kind of input. <b>Three of them are required &mdash; ALS, CRF and Raw data.</b>
        Only <b>USDM</b> is optional. Each card shows what it accepts, lists what you have already uploaded,
        and lets you remove a file with the bin icon.
      </p>
      <img src="assets/docs2/05-step-upload.png" alt="Upload step with ALS, CRF, raw data and USDM cards"
           class="rounded-lg shadow-lg border border-gray-200 mb-8 w-full">

      <ul class="space-y-3 mb-8 text-black">
        <li class="flex gap-3"><span class="text-[#1594fd] font-bold">&bull;</span><span><b>ALS</b> &mdash; required, one file. Choosing another replaces it.</span></li>
        <li class="flex gap-3"><span class="text-[#1594fd] font-bold">&bull;</span><span><b>CRF</b> &mdash; required, one file. The blank PDF.</span></li>
        <li class="flex gap-3"><span class="text-[#1594fd] font-bold">&bull;</span><span><b>Raw data</b> &mdash; required, as many files as you need. The source datasets code generation derives each domain from.</span></li>
        <li class="flex gap-3"><span class="text-[#1594fd] font-bold">&bull;</span><span><b>USDM</b> &mdash; the one optional card, one JSON file. It also carries a <b>Generate USDM JSON from protocol</b> link that takes you to the USDM feature if you do not have one.</span></li>
      </ul>

      <h2 class="text-2xl font-semibold mb-3 text-black">Finishing the step</h2>
      <p class="text-black leading-relaxed font-light">
        <b>Continue</b> becomes available once the ALS and CRF cards each hold a file &mdash; but upload your
        raw datasets before you move on, because code generation later reads them to derive each domain.
        Clicking Continue locks in the inputs and unlocks Trial Design and Annotation. Upload failures show
        a message and change nothing &mdash; just try again.
      </p>
    </section>

    <!-- ======================== TRIAL DESIGN ======================== -->
    <section id="trial-design" class="scroll-mt-16">
      <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">Step 2 &middot; optional</p>
      <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Trial Design datasets</h1>
      <p class="text-black leading-relaxed font-light mb-6">
        This step builds the SDTM trial design domains from your USDM file. It is a <b>side workflow</b>,
        not a gating step &mdash; it sits beside the pipeline rather than inside it, so skipping it blocks
        nothing.
      </p>

      <img src="assets/docs2/06-step-trial-design.png" alt="Trial Design step with the five domain checkboxes"
           class="rounded-lg shadow-lg border border-gray-200 mb-8 w-full">

      <h2 class="text-2xl font-semibold mb-3 text-black">Choose the domains</h2>
      <div class="overflow-x-auto rounded-xl border border-gray-200 mb-6">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
            <tr><th class="px-4 py-3 font-semibold">Code</th><th class="px-4 py-3 font-semibold">Domain</th><th class="px-4 py-3 font-semibold">Contents</th></tr>
          </thead>
          <tbody class="text-black">
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-mono font-semibold text-[#1594fd]">TA</td><td class="px-4 py-3">Trial Arms</td><td class="px-4 py-3 text-gray-700">Planned arms and the elements within each.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-mono font-semibold text-[#1594fd]">TE</td><td class="px-4 py-3">Trial Elements</td><td class="px-4 py-3 text-gray-700">Element definitions and transition rules.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-mono font-semibold text-[#1594fd]">TV</td><td class="px-4 py-3">Trial Visits</td><td class="px-4 py-3 text-gray-700">Planned visits per arm.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-mono font-semibold text-[#1594fd]">TI</td><td class="px-4 py-3">Trial Inclusion/Exclusion</td><td class="px-4 py-3 text-gray-700">Eligibility criteria.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-mono font-semibold text-[#1594fd]">TS</td><td class="px-4 py-3">Trial Summary</td><td class="px-4 py-3 text-gray-700">Study-level summary parameters.</td></tr>
          </tbody>
        </table>
      </div>

      <p class="text-black leading-relaxed font-light mb-4">
        Tick the ones you want and click <b>Generate</b>. Progress is polled every couple of seconds. When
        it finishes, each domain appears as a downloadable CSV with its row count &mdash; <span class="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">ta.csv</span>,
        <span class="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">tv.csv</span> and so on. Every previous run is kept under
        <b>History</b>, with its status, timestamp, domains and its own download buttons, so you can go back to an earlier version.
      </p>

      <div class="rounded-xl border-l-4 border-amber-400 bg-amber-50 px-5 py-4">
        <p class="text-sm text-black leading-relaxed">
          <b>Generate is disabled without a USDM file.</b> The step looks for a JSON file among your uploads.
          If there is not one, either upload it on the Upload step, generate one from your protocol, or click
          <b>Skip this step</b> to move on to Annotation.
        </p>
      </div>
    </section>

    <!-- ========================= ANNOTATION ========================= -->
    <section id="annotation" class="scroll-mt-16">
      <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">Step 3</p>
      <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Annotation</h1>
      <p class="text-black leading-relaxed font-light mb-8">
        The longest step, and the one where your judgement matters most. It runs in three phases: set up
        and launch, wait while predictions are made, then review across three tabs.
      </p>

      <h2 class="text-2xl font-semibold mb-3 text-black">Set up the run</h2>
      <p class="text-black leading-relaxed font-light mb-4">Two choices before you start:</p>
      <div class="grid gap-4 md:grid-cols-2 mb-6">
        <div class="rounded-xl border border-gray-200 p-5">
          <h4 class="font-semibold text-black mb-2">Mode</h4>
          <p class="text-sm text-gray-700 leading-relaxed">
            <b>SDTM</b> maps each CRF field to an SDTM variable for review. <b>Raw</b> places
            source-faithful annotations with no SDTM mapping. SDTM is the default.
          </p>
        </div>
        <div class="rounded-xl border border-gray-200 p-5">
          <h4 class="font-semibold text-black mb-2">Forms</h4>
          <p class="text-sm text-gray-700 leading-relaxed">
            Every form from your ALS, all selected by default, each with its field count. Narrow the
            selection to run a quick trial on one form first. Only the selected forms are annotated.
          </p>
        </div>
      </div>
      <p class="text-black leading-relaxed font-light mb-8">Click <b>Run annotation</b> to start.</p>

      <h2 class="text-2xl font-semibold mb-3 text-black">While it runs</h2>
      <p class="text-black leading-relaxed font-light mb-4">
        A progress card names the step it is in &mdash; reading the ALS, enriching visits, matching anchors,
        predicting annotations, placing annotations &mdash; and shows a running count of fields predicted so far,
        with a percentage once the total is known. The wording changes as it works so you can tell it has not
        stalled. <b>Stop</b> cancels the run and returns you to the setup screen.
      </p>
      <img src="assets/docs2/07-annotation-running.png" alt="Annotation running, 591 of 765 fields predicted so far"
           class="rounded-lg shadow-lg border border-gray-200 mb-4 w-full">
      <p class="text-sm text-gray-600 leading-relaxed mb-6">
        A live run. The Annotation node spins while Spec, Codegen and Results stay padlocked; the banner reads
        <i>Predicting&hellip; 591 fields so far (77% of 765)</i>; tabs 2 and 3 are greyed out and labelled
        <i>after PDF generation</i>. Predictions already made are reviewable underneath &mdash; here 530 were
        auto-approved at high confidence and 61 flagged for review.
      </p>
      <div class="rounded-xl border-l-4 border-[#1594fd] bg-blue-50 px-5 py-4 mb-8">
        <p class="text-sm text-black leading-relaxed">
          Predictions stream in as they are made, so the total keeps climbing while the run is in progress.
          That is normal &mdash; the run is finished when the banner says so, not when the numbers stop moving.
        </p>
      </div>

      <h2 class="text-2xl font-semibold mb-3 text-black">Generating the annotated PDF</h2>
      <p class="text-black leading-relaxed font-light mb-4">
        Once every field has been predicted, the banner reads <i>&ldquo;Step 1 complete &mdash; ready to generate
        the annotated PDF&rdquo;</i>. Review predictions first if you like, then click <b>Generate annotated PDF</b>.
        That runs anchor matching and places the annotations onto the CRF, and it is what unlocks tabs 2 and 3.
        When it is done a green banner summarises the run &mdash; total annotations, fields, pages annotated,
        anchors matched and the domains found.
      </p>

      <div class="rounded-xl border-l-4 border-amber-400 bg-amber-50 px-5 py-4 mb-8">
        <p class="text-sm text-black leading-relaxed">
          <b>Finish your edits before you generate.</b> Confirming, rejecting and especially
          <b>reassigning a target</b> must all be done <i>before</i> you click <b>Generate annotated PDF</b>.
          Once the annotation phase has started, the server refuses further changes to the predictions: the
          edit is rejected and the screen rolls back to the previous value. If you spot a mistake after
          generating, you have to run the annotation again rather than correct it in place.
        </p>
      </div>

      <h2 class="text-2xl font-semibold mb-4 text-black">Tab 1 &mdash; Prediction Review</h2>
      <p class="text-black leading-relaxed font-light mb-4">
        A queue on the left, the selected field's detail on the right. High-confidence predictions are
        auto-approved and a banner tells you how many were flagged for a closer look.
      </p>
      <img src="assets/docs2/08-annotation-prediction.png" alt="Prediction Review tab with the field queue and detail pane"
           class="rounded-lg shadow-lg border border-gray-200 mb-6 w-full">
      <p class="text-black leading-relaxed font-light mb-4">
        Filter with <b>All</b>, <b>Needs attention</b>, <b>Unconfirmed</b> or <b>Confirmed</b>; each carries a
        live count. <i>Needs attention</i> is the one to work through &mdash; it collects everything not yet
        confirmed whose confidence is medium, low or unknown.
      </p>
      <p class="text-black leading-relaxed font-light mb-4">
        The detail pane shows the source question as it appears on the CRF, the predicted
        <span class="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">DOMAIN.VARIABLE</span> target with its
        confidence, and the reasoning behind it. Three actions:
      </p>
      <ul class="space-y-2 mb-4 text-black">
        <li class="flex gap-3"><span class="text-emerald-600 font-bold">&bull;</span><span><b>Confirm</b> &mdash; accept the prediction.</span></li>
        <li class="flex gap-3"><span class="text-[#1594fd] font-bold">&bull;</span><span><b>Edit target</b> &mdash; type the correct domain and variable. Saving marks the field confirmed. If the save is rejected the change is rolled back rather than left looking saved.</span></li>
        <li class="flex gap-3"><span class="text-red-600 font-bold">&bull;</span><span><b>Reject</b> &mdash; discard the prediction. Rejected fields stay in the pool for <b>Confirm all</b>.</span></li>
      </ul>
      <p class="text-black leading-relaxed font-light mb-8">
        <b>Confirm all</b> accepts everything still outstanding in one go.
      </p>

      <h2 class="text-2xl font-semibold mb-4 text-black">Tab 2 &mdash; Anchor Resolution</h2>
      <p class="text-black leading-relaxed font-light mb-4">
        An anchor is the piece of text used to find a field on the CRF page. When the text in the ALS does
        not match the PDF exactly, the anchor is unresolved and its annotation cannot be placed. This tab is
        where you fix those.
      </p>
      <p class="text-black leading-relaxed font-light mb-4">
        Anchors are grouped into <b>Unresolved</b>, <b>Resolved</b> and <b>No match</b>. Selecting one shows the
        actual CRF page with the expected region outlined. On the right you can pick a suggested candidate
        match with its score, type the exact wording as it appears in the PDF and <b>Submit correction</b>, reuse
        the ALS text with one click, or <b>Mark as no-match</b> when the field genuinely is not on the page.
      </p>
      <img src="assets/docs2/09-annotation-anchor.png" alt="Anchor Resolution tab with the anchor list, CRF viewer and correction panel"
           class="rounded-lg shadow-lg border border-gray-200 mb-4 w-full">
      <p class="text-sm text-gray-600 leading-relaxed mb-8">
        The counter beside the tabs tracks your progress &mdash; in this run 747 of 765 anchors matched
        automatically, leaving 18 to resolve by hand.
      </p>

      <h2 class="text-2xl font-semibold mb-4 text-black">Tab 3 &mdash; Placement Review</h2>
      <p class="text-black leading-relaxed font-light mb-4">
        The finished annotated CRF. The sidebar lists every placed annotation grouped by page; clicking one
        jumps to that page and outlines it. Page through with the arrows, zoom between 50% and 250%, and
        click the zoom figure to reset it to 100%.
      </p>
      <img src="assets/docs2/10-annotation-placement.png" alt="Placement Review tab showing the annotated CRF page with placed annotation boxes"
           class="rounded-lg shadow-lg border border-gray-200 mb-4 w-full">
      <p class="text-sm text-gray-600 leading-relaxed mb-6">
        Each annotation is drawn onto the real page &mdash; here <span class="font-mono text-xs">DM.SUBJID</span>
        sits against the subject identifier field, with the domain labels
        <span class="font-mono text-xs">DM (Demographics)</span> and
        <span class="font-mono text-xs">SC (Subject Characteristics)</span> at the top of the form.
      </p>
      <p class="text-black leading-relaxed font-light mb-8">
        <b>Annotated CRF (PDF)</b> downloads the finished document.
      </p>

      <div class="rounded-xl border-l-4 border-emerald-400 bg-emerald-50 px-5 py-4">
        <p class="text-sm text-black leading-relaxed">
          When you are satisfied, click <b>Continue to Spec</b>. It only becomes available once the annotated
          PDF exists.
        </p>
      </div>
    </section>

    <!-- ============================ SPEC ============================ -->
    <section id="spec" class="scroll-mt-16">
      <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">Step 4</p>
      <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Spec review</h1>
      <p class="text-black leading-relaxed font-light mb-6">
        Formally the <b>Spec Validation</b> step. Annotation produces a mapping specification, and this is
        your chance to correct it before any code is written against it. Counts of domains and variables sit at the top; a badge shows what produced the spec.
      </p>

      <h2 class="text-2xl font-semibold mb-3 text-black">Table view</h2>
      <p class="text-black leading-relaxed font-light mb-4">
        Pick a domain from the dropdown &mdash; each entry shows how many populated variables it has &mdash; and
        its variables fill the table. <b>Name</b>, <b>Label</b>, <b>Type</b> and <b>Role</b> are read-only.
        The five columns highlighted in blue are editable: <b>Core</b>, <b>Origin</b>, <b>Codelist</b>,
        <b>Source refs</b> and <b>Derivation</b>.
      </p>
      <p class="text-black leading-relaxed font-light mb-4">
        Click a cell to edit it. <span class="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">Enter</span>
        or clicking away commits the change; <span class="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">Esc</span>
        cancels. Source refs are comma-separated. Only variables the mapping actually populates are listed.
      </p>
      <img src="assets/docs2/11-spec-table.png" alt="Spec review table view with the domain dropdown and editable columns"
           class="rounded-lg shadow-lg border border-gray-200 mb-8 w-full">

      <h2 class="text-2xl font-semibold mb-3 text-black">Raw YAML view</h2>
      <p class="text-black leading-relaxed font-light mb-6">
        Switch to <b>Raw YAML</b> to edit the whole spec as text &mdash; useful for bulk changes. A live indicator
        tells you whether the YAML is valid; it must parse, contain a <span class="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">domains:</span>
        list and use spaces rather than tabs. Approval is blocked while it is invalid. The two views stay in
        sync, so edits in one appear in the other.
      </p>
      <img src="assets/docs2/12-spec-raw-yaml.png" alt="Spec review raw YAML editor with the validity indicator"
           class="rounded-lg shadow-lg border border-gray-200 mb-8 w-full">

      <h2 class="text-2xl font-semibold mb-3 text-black">Download and approve</h2>
      <p class="text-black leading-relaxed font-light mb-6">
        <b>Download</b> exports the current spec &mdash; including your unsaved edits &mdash; as CSV for Excel, YAML,
        or JSON.
      </p>
      <div class="rounded-xl border-l-4 border-emerald-400 bg-emerald-50 px-5 py-4 mb-6">
        <p class="text-sm text-black leading-relaxed">
          <b>Approve spec</b> writes the spec and unlocks code generation. An <i>Approved</i> badge appears and
          the button becomes <b>Continue to Codegen</b>.
        </p>
      </div>
      <img src="assets/docs2/13-spec-approved.png" alt="Spec review after approval, showing the Approved badge and Continue to Codegen"
           class="rounded-lg shadow-lg border border-gray-200 w-full">
    </section>

    <!-- ========================== CODEGEN =========================== -->
    <section id="codegen" class="scroll-mt-16">
      <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">Step 5</p>
      <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Code generation</h1>
      <p class="text-black leading-relaxed font-light mb-6">
        An AI agent writes R code (sdtm.oak) that derives one SDTM domain from your raw data, runs it,
        checks the result, and fixes its own mistakes before finishing. You generate <b>one domain at a time</b>.
      </p>

      <h2 class="text-2xl font-semibold mb-3 text-black">Running it</h2>
      <p class="text-black leading-relaxed font-light mb-4">
        Choose a domain from the dropdown &mdash; the list comes from your approved spec, each with its current
        state &mdash; and click <b>Generate</b>. Before you start, all four cards sit <i>Idle</i> and the panel
        invites you to press Generate.
      </p>
      <img src="assets/docs2/14-codegen-idle.png" alt="Code generation before starting, with the domain dropdown and four idle agent cards"
           class="rounded-lg shadow-lg border border-gray-200 mb-6 w-full">
      <p class="text-black leading-relaxed font-light mb-6">Four cards track the agent as it works:</p>
      <ol class="space-y-2 mb-6 text-black">
        <li class="flex gap-3"><span class="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 text-gray-700 text-xs font-bold flex items-center justify-center">1</span><span><b>Inspect Data</b> &mdash; reads the raw datasets and the spec to plan the mapping.</span></li>
        <li class="flex gap-3"><span class="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 text-gray-700 text-xs font-bold flex items-center justify-center">2</span><span><b>Run R</b> &mdash; writes the derivation script and executes it.</span></li>
        <li class="flex gap-3"><span class="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 text-gray-700 text-xs font-bold flex items-center justify-center">3</span><span><b>Validate Dataset</b> &mdash; checks the derived dataset against CDISC rules.</span></li>
        <li class="flex gap-3"><span class="flex-shrink-0 w-6 h-6 rounded-full bg-gray-200 text-gray-700 text-xs font-bold flex items-center justify-center">4</span><span><b>Finalize Script</b> &mdash; gates and finalises the script.</span></li>
      </ol>
      <p class="text-black leading-relaxed font-light mb-4">
        <b>Agent Thoughts</b> streams its reasoning live underneath. When it finishes you get the row count of
        the derived dataset, the number of blocking findings, the CDISC findings count and the generated script,
        with a <i>draft</i> or <i>failed</i> badge if it did not fully succeed.
      </p>
      <img src="assets/docs2/15-codegen-running.png" alt="Code generation mid-run: Inspect Data card active and agent thoughts streaming"
           class="rounded-lg shadow-lg border border-gray-200 mb-4 w-full">
      <p class="text-sm text-gray-600 leading-relaxed mb-6">
        A live run for the AE domain. The domain badge reads <i>Running</i>, a <b>Stop</b> button appears beside
        it, the <b>Inspect Data</b> card is lit while the other three wait, and the agent narrates what it is
        doing &mdash; here reading <span class="font-mono text-xs">raw/ae.csv</span> and listing the columns it found.
      </p>

      <div class="rounded-xl border-l-4 border-red-400 bg-red-50 px-5 py-4 mb-6">
        <p class="text-sm text-black leading-relaxed">
          <b>Keep this tab open and focused until the run finishes.</b> Your browser is what actually writes
          the script and runs R inside your study workspace &mdash; the agent waits on it. Closing the tab or
          navigating away mid-run leaves the job stranded. Use <b>Stop</b> if you need to abandon it, which
          cancels cleanly.
        </p>
      </div>

      <p class="text-black leading-relaxed font-light">
        Repeat for each domain you need. <b>Download</b> saves the R script on its own; everything else is
        collected on the next step. Once at least one domain has finished, <b>Continue to Results</b> appears.
      </p>
    </section>

    <!-- ========================== RESULTS =========================== -->
    <section id="results" class="scroll-mt-16">
      <p class="text-sm font-semibold uppercase tracking-widest text-[#1594fd] mb-2">Step 6</p>
      <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Output and deliverables</h1>
      <p class="text-black leading-relaxed font-light mb-6">
        The finished product. Everything the pipeline produced, in one place.
      </p>
      <img src="assets/docs2/16-results.png" alt="Output and Deliverables with the run summary and study-level downloads"
           class="rounded-lg shadow-lg border border-gray-200 mb-4 w-full">
      <p class="text-sm text-gray-600 leading-relaxed mb-8">
        All six timeline nodes are now green. The run summary reports 1028 annotations across 90 of 91 pages,
        738 of 756 ALS fields matched (97.6%) over 56 forms, in 31m 31s. The domains table is empty here because
        code generation had not produced a domain yet &mdash; once it has, each domain appears as a row with its
        own downloads.
      </p>

      <h2 class="text-2xl font-semibold mb-3 text-black">Generated SDTM domains</h2>
      <p class="text-black leading-relaxed font-light mb-4">
        One row per domain you generated, each with up to three downloads: the <b>R code</b> that derives it,
        the derived <b>Dataset (CSV)</b>, and the <b>CDISC findings</b> from validation. Domains that produced
        nothing are marked <i>No artifacts</i>.
      </p>

      <h2 class="text-2xl font-semibold mb-3 text-black">Run summary</h2>
      <p class="text-black leading-relaxed font-light mb-4">
        A record of the annotation run: when it started and finished, how long it took, how many steps
        completed, the number of annotations created, pages annotated out of the total, how many ALS fields
        were matched to the CRF and at what rate, the number of forms, and the domains covered. If the run
        failed, the error and the step it failed at are shown here.
      </p>

      <h2 class="text-2xl font-semibold mb-3 text-black">Study-level downloads</h2>
      <p class="text-black leading-relaxed font-light mb-6">
        Once the run has completed you can download the <b>Annotated CRF (PDF)</b> and the <b>Mapping CSV</b>.
        Before then the section reads <i>&ldquo;Downloads available once the run completes&rdquo;</i>.
      </p>

      <div class="rounded-xl border-l-4 border-emerald-400 bg-emerald-50 px-5 py-4">
        <p class="text-sm text-black leading-relaxed">
          <b>Complete study</b> marks the work finished. The study's badge changes to <i>Completed</i>, its
          next action becomes <i>Signed off</i>, and it is treated as locked. Download anything you need first.
        </p>
      </div>
    </section>

    <!-- ========================= STATUSES =========================== -->
    <section id="statuses" class="scroll-mt-16">
      <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Step statuses</h1>
      <p class="text-black leading-relaxed font-light mb-6">The same six states appear on the timeline, the dots and the rows.</p>
      <div class="overflow-x-auto rounded-xl border border-gray-200 mb-8">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
            <tr><th class="px-4 py-3 font-semibold">Status</th><th class="px-4 py-3 font-semibold">Looks like</th><th class="px-4 py-3 font-semibold">What to do</th></tr>
          </thead>
          <tbody class="text-black">
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-semibold">Locked</td><td class="px-4 py-3 text-gray-700">Grey, padlock, not clickable</td><td class="px-4 py-3 text-gray-700">Finish the step before it. The panel tells you which.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-semibold">Ready</td><td class="px-4 py-3 text-gray-700">Numbered, hollow ring</td><td class="px-4 py-3 text-gray-700">Your turn &mdash; open it and act.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-semibold">Running</td><td class="px-4 py-3 text-gray-700">Blue, spinning</td><td class="px-4 py-3 text-gray-700">Wait. The page updates itself.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-semibold">Completed</td><td class="px-4 py-3 text-gray-700">Green tick</td><td class="px-4 py-3 text-gray-700">Nothing. You can still reopen it to look.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-semibold">Failed</td><td class="px-4 py-3 text-gray-700">Red warning triangle</td><td class="px-4 py-3 text-gray-700">Open it, read the error, and retry.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-semibold">Needs rerun</td><td class="px-4 py-3 text-gray-700">Amber refresh arrow</td><td class="px-4 py-3 text-gray-700">Something upstream changed &mdash; run this step again.</td></tr>
          </tbody>
        </table>
      </div>

      <h2 class="text-2xl font-semibold mb-3 text-black">What unlocks what</h2>
      <ul class="space-y-2 text-black">
        <li class="flex gap-3"><span class="text-[#1594fd] font-bold">&bull;</span><span><b>Trial Design</b> and <b>Annotation</b> unlock once study inputs are uploaded.</span></li>
        <li class="flex gap-3"><span class="text-[#1594fd] font-bold">&bull;</span><span><b>Spec</b> unlocks after annotation completes.</span></li>
        <li class="flex gap-3"><span class="text-[#1594fd] font-bold">&bull;</span><span><b>Codegen</b> unlocks after the spec is approved.</span></li>
        <li class="flex gap-3"><span class="text-[#1594fd] font-bold">&bull;</span><span><b>Results</b> unlocks after code generation completes.</span></li>
      </ul>
    </section>

    <!-- ========================== OUTPUTS =========================== -->
    <section id="outputs" class="scroll-mt-16">
      <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Every file you can download</h1>
      <div class="overflow-x-auto rounded-xl border border-gray-200">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
            <tr><th class="px-4 py-3 font-semibold">File</th><th class="px-4 py-3 font-semibold">Where</th><th class="px-4 py-3 font-semibold">Contents</th></tr>
          </thead>
          <tbody class="text-black">
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-mono text-xs">ta.csv, te.csv, tv.csv, ti.csv, ts.csv</td><td class="px-4 py-3">Trial Design</td><td class="px-4 py-3 text-gray-700">Trial design domain datasets.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-mono text-xs">annotated CRF (PDF)</td><td class="px-4 py-3">Annotation tab 3, and Results</td><td class="px-4 py-3 text-gray-700">The CRF with SDTM annotations placed on it.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-mono text-xs">spec (CSV / YAML / JSON)</td><td class="px-4 py-3">Spec review</td><td class="px-4 py-3 text-gray-700">The mapping specification, including your edits.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-mono text-xs">domain R script</td><td class="px-4 py-3">Codegen and Results</td><td class="px-4 py-3 text-gray-700">The sdtm.oak derivation code for one domain.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-mono text-xs">derived dataset (CSV)</td><td class="px-4 py-3">Results</td><td class="px-4 py-3 text-gray-700">The SDTM dataset the script produced.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-mono text-xs">CDISC findings (JSON)</td><td class="px-4 py-3">Results</td><td class="px-4 py-3 text-gray-700">Validation findings for that domain.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3 font-mono text-xs">mapping CSV</td><td class="px-4 py-3">Results</td><td class="px-4 py-3 text-gray-700">The field-to-variable mapping for the whole study.</td></tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- =========================== ADMIN ============================ -->
    <section id="admin" class="scroll-mt-16">
      <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Standards Library</h1>
      <p class="text-black leading-relaxed font-light">
        Administrators maintain the CDISC standards the pipeline maps against &mdash; <b>SDTM IG</b>,
        <b>Controlled Terminology</b> and <b>MSG</b> &mdash; on the separate <b>Standards Library</b> screen. The versions
        installed there are what appear in the dropdowns when you create a study. There is no link to it in
        the menu; it is reached directly at <span class="font-mono text-xs bg-gray-100 px-1.5 py-0.5 rounded">/sdtm/admin/standards</span>.
      </p>
    </section>

    <!-- ======================= TROUBLESHOOTING ====================== -->
    <section id="troubleshooting" class="scroll-mt-16">
      <h1 class="text-4xl font-bold mb-6 text-black tracking-tight">Troubleshooting</h1>
      <div class="overflow-x-auto rounded-xl border border-gray-200">
        <table class="w-full text-sm">
          <thead class="bg-gray-50 text-left text-[11px] uppercase tracking-wider text-gray-500">
            <tr><th class="px-4 py-3 font-semibold">Message</th><th class="px-4 py-3 font-semibold">Why</th><th class="px-4 py-3 font-semibold">Fix</th></tr>
          </thead>
          <tbody class="text-black">
            <tr class="border-t border-gray-100"><td class="px-4 py-3">Please connect the repository to access SDTM</td><td class="px-4 py-3 text-gray-700">No repository connected in this tab.</td><td class="px-4 py-3 text-gray-700">Connect one from the dashboard drawer.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3">Select a workspace repository first</td><td class="px-4 py-3 text-gray-700">Same cause, seen on the create-study form.</td><td class="px-4 py-3 text-gray-700">Connect a repository, then return.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3">Cannot reach the server</td><td class="px-4 py-3 text-gray-700">Network problem or the service is down.</td><td class="px-4 py-3 text-gray-700">Check your connection and retry.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3">Your session has expired</td><td class="px-4 py-3 text-gray-700">Sign-in no longer valid.</td><td class="px-4 py-3 text-gray-700">Sign in again, reconnect the study.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3">The file is too large to upload</td><td class="px-4 py-3 text-gray-700">Upload exceeded the size limit.</td><td class="px-4 py-3 text-gray-700">Reduce or split the file.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3">CDISC catalog unavailable</td><td class="px-4 py-3 text-gray-700">Standards versions could not be loaded.</td><td class="px-4 py-3 text-gray-700">Ask an administrator to check the CDISC Library credentials.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3">No spec available yet</td><td class="px-4 py-3 text-gray-700">Spec opened before annotation produced one.</td><td class="px-4 py-3 text-gray-700">Run annotation to completion first.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3">Confirmed locally, but saving failed</td><td class="px-4 py-3 text-gray-700">Your review was not persisted.</td><td class="px-4 py-3 text-gray-700">Redo that confirmation once the connection recovers.</td></tr>
            <tr class="border-t border-gray-100"><td class="px-4 py-3">Could not read the study's USDM file</td><td class="px-4 py-3 text-gray-700">Trial Design could not load the JSON.</td><td class="px-4 py-3 text-gray-700">Re-upload the USDM file, or skip the step.</td></tr>
          </tbody>
        </table>
      </div>
    </section>

    <!-- ========================= FAQ (SDTM) ========================= -->
    <section id="faq-sdtm" class="scroll-mt-16">
      <h1 class="text-4xl font-bold mb-8 text-black tracking-tight">FAQ</h1>
      <h3 class="text-sm font-bold uppercase tracking-wider text-gray-400 mb-4">SDTM</h3>
      <div class="grid gap-5 md:grid-cols-2">
        <div class="p-6 rounded-xl border border-gray-200 hover:border-[#1594fd] transition-all duration-300 hover:shadow-lg">
          <h3 class="text-lg font-semibold mb-2 text-black">Can I leave and come back later?</h3>
          <p class="text-sm text-gray-700 leading-relaxed">Yes. Progress is saved against the study. The exception is code generation, which needs the tab to stay open while it runs.</p>
        </div>
        <div class="p-6 rounded-xl border border-gray-200 hover:border-[#1594fd] transition-all duration-300 hover:shadow-lg">
          <h3 class="text-lg font-semibold mb-2 text-black">Do I have to review every prediction?</h3>
          <p class="text-sm text-gray-700 leading-relaxed">No. High-confidence predictions are auto-approved, and <b>Confirm all</b> accepts the rest. Working through <i>Needs attention</i> is the sensible middle ground.</p>
        </div>
        <div class="p-6 rounded-xl border border-gray-200 hover:border-[#1594fd] transition-all duration-300 hover:shadow-lg">
          <h3 class="text-lg font-semibold mb-2 text-black">Can I change the spec after approving it?</h3>
          <p class="text-sm text-gray-700 leading-relaxed">The approved spec is what code generation uses. Reopen the Spec step to view it; re-approving after edits regenerates from the new version.</p>
        </div>
        <div class="p-6 rounded-xl border border-gray-200 hover:border-[#1594fd] transition-all duration-300 hover:shadow-lg">
          <h3 class="text-lg font-semibold mb-2 text-black">Can I generate all domains at once?</h3>
          <p class="text-sm text-gray-700 leading-relaxed">No &mdash; one domain per run. Each is independent, so you can generate only the ones you need.</p>
        </div>
        <div class="p-6 rounded-xl border border-gray-200 hover:border-[#1594fd] transition-all duration-300 hover:shadow-lg">
          <h3 class="text-lg font-semibold mb-2 text-black">What if I have no USDM file?</h3>
          <p class="text-sm text-gray-700 leading-relaxed">Skip Trial Design, or generate a USDM from your protocol PDF using the USDM Builder (there is a link on the Upload step). It blocks nothing.</p>
        </div>
        <div class="p-6 rounded-xl border border-gray-200 hover:border-[#1594fd] transition-all duration-300 hover:shadow-lg">
          <h3 class="text-lg font-semibold mb-2 text-black">What happens when I complete a study?</h3>
          <p class="text-sm text-gray-700 leading-relaxed">It is marked Completed and treated as locked. Download everything you need before completing it.</p>
        </div>
      </div>
    </section>
  }

</div>
